import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import { StorageLink } from '../global/storageUrls';

function formatSubstanceName(substance) {
  if (substance === 'sealNumber') return 'Seal Number';
  return substance
    .replace(/_/g, ' ')
    .replace(/(\d+)/g, ' $1')
    .replace(/ml/g, 'mL')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

function HistoryModal({ config, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from(config.tableName)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setHistory(data || []);
    } catch (err) {
      console.error('Error fetching history:', err);
      alert('Error loading history.');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (history.length === 0) {
      alert('No data to download');
      return;
    }

    // Build CSV header
    let csv = 'Date,Time,Location,First Name,Last Name,Email,Seal Intact,Reason for Broken Seal,Seal Number,Primary Signature,Secondary Name,Secondary Signature,Inventory Picture,Additional Notes';

    config.substances.forEach(substance => {
      csv += `,${formatSubstanceName(substance)}`;
      csv += `,${formatSubstanceName(substance)} Exp`;
    });
    csv += '\n';

    // Build CSV rows
    history.forEach(record => {
      const date = new Date(record.created_at);
      const formattedDate = date.toLocaleDateString('en-US');
      const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

      let row = [
        formattedDate,
        formattedTime,
        record.location || 'N/A',
        record.first_name || 'N/A',
        record.last_name || 'N/A',
        record.email || 'N/A',
        record.seal_intact || 'N/A',
        record.reason_broken_seal || 'N/A',
        record.seal_number || 'N/A',
        record.primary_signature_url || 'N/A',
        record.secondary_name || 'N/A',
        record.secondary_signature_url || 'N/A',
        record.inventory_picture_url || 'N/A',
        record.additional_notes || 'N/A'
      ];

      config.substances.forEach(substance => {
        row.push(record[substance] || 'N/A');
        row.push(record[`${substance}_exp`] || 'N/A');
      });

      // Escape quotes and wrap in quotes
      row = row.map(field => {
        const str = String(field).replace(/"/g, '""');
        return `"${str}"`;
      });

      csv += row.join(',') + '\n';
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${config.locationName}_history.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.7)',
      overflowY: 'auto',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '15px',
        padding: '40px',
        maxWidth: '95%',
        margin: '0 auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <h2 style={{ color: '#333', fontSize: '24px', margin: 0 }}>
            Inventory History - {config.locationName}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Download Button */}
        <div style={{ marginBottom: '20px' }}>
          <button
            className="btn btn-primary"
            onClick={downloadCSV}
            disabled={history.length === 0}
          >
            Download CSV
          </button>
        </div>

        {/* History Table */}
        {loading ? (
          <p style={{ textAlign: 'center', color: '#666' }}>Loading history...</p>
        ) : history.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666' }}>No history records found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ background: '#0E3293', color: 'white' }}>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Time</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>First Name</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Last Name</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Seal Intact</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Seal Number</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Reason</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Secondary Name</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Primary Sig</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Secondary Sig</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Picture</th>
                  {config.substances.map(substance => (
                    <React.Fragment key={substance}>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>
                        {formatSubstanceName(substance)}
                      </th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>
                        Exp
                      </th>
                    </React.Fragment>
                  ))}
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record, index) => {
                  const date = new Date(record.created_at);
                  const formattedDate = date.toLocaleDateString('en-US');
                  const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

                  return (
                    <tr key={index} style={{ background: index % 2 === 0 ? '#f8f9fa' : 'white' }}>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{formattedDate}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{formattedTime}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{record.first_name || 'N/A'}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{record.last_name || 'N/A'}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{record.seal_intact || 'N/A'}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{record.seal_number || 'N/A'}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{record.reason_broken_seal || 'N/A'}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{record.secondary_name || 'N/A'}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                        {record.primary_signature_url ? (
                          <StorageLink value={record.primary_signature_url} style={{ color: '#0E3293' }}>
                            View
                          </StorageLink>
                        ) : 'N/A'}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                        {record.secondary_signature_url ? (
                          <StorageLink value={record.secondary_signature_url} style={{ color: '#0E3293' }}>
                            View
                          </StorageLink>
                        ) : 'N/A'}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                        {record.inventory_picture_url ? (
                          <StorageLink value={record.inventory_picture_url} style={{ color: '#0E3293' }}>
                            View
                          </StorageLink>
                        ) : 'N/A'}
                      </td>
                      {config.substances.map(substance => (
                        <React.Fragment key={substance}>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{record[substance] || '0'}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{record[`${substance}_exp`] || 'N/A'}</td>
                        </React.Fragment>
                      ))}
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{record.additional_notes || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default HistoryModal;
